import { messageErreur } from '@imp/shared/message-erreur';
import { filtrerRecherche } from '@imp/shared/recherche';
import { useQuery } from '@tanstack/react-query';
import { Plus, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { BarreRecherche } from '@/components/BarreRecherche';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Select } from '@/components/ui/select';
import { contactDisplayName, listContacts } from '@/modules/clients/api';
import { CHANTIER_STATUT_LABELS, listChantiers } from '@/modules/chantiers/api';

const STATUT_CLASSES: Record<string, string> = {
  prospect: 'bg-muted text-muted-foreground',
  planifie: 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300',
  en_cours: 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300',
  pause: 'bg-muted text-muted-foreground',
  termine: 'bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300',
  receptionne: 'bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300',
  sav: 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300',
  archive: 'bg-muted text-muted-foreground',
  annule: 'bg-muted text-muted-foreground',
};

const ALL = '__all__';

export function ChantiersListPage() {
  const chantiersQ = useQuery({ queryKey: ['chantiers'], queryFn: listChantiers });
  const contactsQ = useQuery({ queryKey: ['contacts'], queryFn: listContacts });
  const contactMap = useMemo(
    () => new Map((contactsQ.data ?? []).map((c) => [c.id, c])),
    [contactsQ.data],
  );
  const chantiers = useMemo(() => chantiersQ.data ?? [], [chantiersQ.data]);

  const [q, setQ] = useState('');
  const [statut, setStatut] = useState<string>(ALL);
  const [ville, setVille] = useState<string>(ALL);
  const [type, setType] = useState<string>(ALL);

  // Options de filtres dérivées des données réelles.
  const statuts = useMemo(
    () => [...new Set(chantiers.map((c) => c.statut))].sort(),
    [chantiers],
  );
  const villes = useMemo(
    () => [...new Set(chantiers.map((c) => c.ville).filter((v): v is string => !!v))].sort((a, b) => a.localeCompare(b, 'fr')),
    [chantiers],
  );
  const types = useMemo(
    () => [...new Set(chantiers.map((c) => c.type_travaux).filter((t): t is string => !!t))].sort((a, b) => a.localeCompare(b, 'fr')),
    [chantiers],
  );

  // D'abord les filtres par catégorie, puis la recherche universelle sur ce
  // qui reste : le compteur de résultats parle alors bien du texte tapé.
  const filtered = useMemo(() => {
    const parCategorie = chantiers.filter((ch) => {
      if (statut !== ALL && ch.statut !== statut) return false;
      if (ville !== ALL && (ch.ville ?? '') !== ville) return false;
      if (type !== ALL && (ch.type_travaux ?? '') !== type) return false;
      return true;
    });
    return filtrerRecherche(parCategorie, q, (ch) => {
      const c = ch.contact_id ? contactMap.get(ch.contact_id) : undefined;
      return [
        ch.numero,
        ch.libelle,
        c ? contactDisplayName(c) : null,
        ch.ville,
        ch.type_travaux,
        CHANTIER_STATUT_LABELS[ch.statut] ?? ch.statut,
      ];
    });
  }, [chantiers, contactMap, q, statut, ville, type]);

  const hasFilter = q.trim() !== '' || statut !== ALL || ville !== ALL || type !== ALL;
  const reset = () => {
    setQ('');
    setStatut(ALL);
    setVille(ALL);
    setType(ALL);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Chantiers</h1>
          <p className="text-muted-foreground">Suivi des chantiers : localisation, équipe, photos.</p>
        </div>
        <Button asChild>
          <Link to="/chantiers/nouveau">
            <Plus className="h-4 w-4" /> Nouveau chantier
          </Link>
        </Button>
      </div>

      {/* Recherche + filtres par catégories */}
      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
        <BarreRecherche
          valeur={q}
          onChange={setQ}
          quoi="un chantier"
          champs="n°, libellé, client, ville"
          className="w-full sm:w-72"
        />

        <div className="space-y-1.5">
          <label htmlFor="ch-statut" className="text-xs font-medium text-muted-foreground">
            Statut
          </label>
          <Select
            id="ch-statut"
            value={statut}
            onChange={(e) => {
              setStatut(e.target.value);
            }}
            className="sm:w-44"
          >
            <option value={ALL}>Tous les statuts</option>
            {statuts.map((s) => (
              <option key={s} value={s}>
                {CHANTIER_STATUT_LABELS[s] ?? s}
              </option>
            ))}
          </Select>
        </div>

        <div className="space-y-1.5">
          <label htmlFor="ch-type" className="text-xs font-medium text-muted-foreground">
            Type de travaux
          </label>
          <Select
            id="ch-type"
            value={type}
            onChange={(e) => {
              setType(e.target.value);
            }}
            className="sm:w-52"
          >
            <option value={ALL}>Tous les types</option>
            {types.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </Select>
        </div>

        <div className="space-y-1.5">
          <label htmlFor="ch-ville" className="text-xs font-medium text-muted-foreground">
            Ville
          </label>
          <Select
            id="ch-ville"
            value={ville}
            onChange={(e) => {
              setVille(e.target.value);
            }}
            className="sm:w-48"
          >
            <option value={ALL}>Toutes les villes</option>
            {villes.map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </Select>
        </div>

        {hasFilter ? (
          <Button variant="outline" onClick={reset} className="sm:mb-0">
            <X className="h-4 w-4" /> Réinitialiser
          </Button>
        ) : null}
      </div>

      {chantiersQ.isLoading ? (
        <p className="text-muted-foreground">Chargement…</p>
      ) : chantiersQ.isError ? (
        <p className="text-destructive">Erreur : {messageErreur(chantiersQ.error)}</p>
      ) : chantiers.length === 0 ? (
        <Card className="flex flex-col items-center justify-center gap-3 py-16 text-center">
          <p className="text-muted-foreground">Aucun chantier pour le moment.</p>
          <Button asChild>
            <Link to="/chantiers/nouveau">
              <Plus className="h-4 w-4" /> Créer le premier chantier
            </Link>
          </Button>
        </Card>
      ) : (
        <>
          <p className="text-sm text-muted-foreground">
            {filtered.length} chantier{filtered.length > 1 ? 's' : ''}
            {hasFilter ? ` sur ${chantiers.length}` : ''}
          </p>
          {filtered.length === 0 ? (
            <Card className="flex flex-col items-center justify-center gap-3 py-16 text-center">
              <p className="text-muted-foreground">Aucun chantier ne correspond à la recherche.</p>
              <Button variant="outline" onClick={reset}>
                <X className="h-4 w-4" /> Réinitialiser les filtres
              </Button>
            </Card>
          ) : (
            <Card className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b bg-muted/50 text-left text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3 font-medium">Numéro</th>
                    <th className="px-4 py-3 font-medium">Libellé</th>
                    <th className="px-4 py-3 font-medium">Client</th>
                    <th className="px-4 py-3 font-medium">Type</th>
                    <th className="px-4 py-3 font-medium">Ville</th>
                    <th className="px-4 py-3 font-medium">Statut</th>
                    <th className="px-4 py-3 font-medium">Avancement</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((ch) => {
                    const c = ch.contact_id ? contactMap.get(ch.contact_id) : undefined;
                    return (
                      <tr key={ch.id} className="border-b last:border-0 hover:bg-muted/40">
                        <td className="px-4 py-3 font-mono text-xs">
                          <Link to={`/chantiers/${ch.id}`} className="text-primary hover:underline">
                            {ch.numero}
                          </Link>
                        </td>
                        <td className="px-4 py-3 font-medium">{ch.libelle}</td>
                        <td className="px-4 py-3 text-muted-foreground">
                          {c ? contactDisplayName(c) : '—'}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">{ch.type_travaux ?? '—'}</td>
                        <td className="px-4 py-3 text-muted-foreground">{ch.ville ?? '—'}</td>
                        <td className="px-4 py-3">
                          <span
                            className={`rounded px-2 py-0.5 text-xs font-medium ${STATUT_CLASSES[ch.statut] ?? 'bg-muted text-muted-foreground'}`}
                          >
                            {CHANTIER_STATUT_LABELS[ch.statut] ?? ch.statut}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="h-1.5 w-20 overflow-hidden rounded-full bg-muted">
                              <div
                                className="h-full rounded-full bg-primary"
                                style={{ width: `${Math.min(100, ch.avancement_pourcentage)}%` }}
                              />
                            </div>
                            <span className="text-xs tabular-nums text-muted-foreground">
                              {ch.avancement_pourcentage}%
                            </span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
